import { useCallback, useEffect, useRef, useState } from "react";

// Leaflet loaded from CDN (see index.html) — available as window.L.
declare const L: any;

interface Venue {
  id: number;
  name: string | null;
  url: string | null;
  lat: number;
  lng: number;
  type: string | null;
  vegan: boolean;
  vegOnly: boolean;
  rating: number | null;
  reviews: number | null;
  address: string | null;
  tags: string[];
}

const PARIS = { lat: 48.8566, lng: 2.3522 };
const COOKIE_KEY = "hc_cookie";

// Marker colour by veg level.
function colorFor(v: Venue): string {
  if (v.vegan) return "#16a34a"; // green = vegan
  if (v.vegOnly) return "#ca8a04"; // amber = vegetarian only
  return "#6b7280"; // grey = veg options
}

export function VeggieMap() {
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);

  const [center, setCenter] = useState(PARIS);
  const [radius, setRadius] = useState(3); // miles (HappyCow `s`)
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cookie, setCookie] = useState<string>(() => {
    try {
      return localStorage.getItem(COOKIE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [showCookie, setShowCookie] = useState(false);

  // Init the Leaflet map once.
  useEffect(() => {
    if (!mapEl.current || mapRef.current || typeof L === "undefined") return;
    const map = L.map(mapEl.current).setView([center.lat, center.lng], 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchVenues = useCallback(
    async (lat: number, lng: number, s: number, ck: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `/api/venues/all?lat=${lat}&lng=${lng}&s=${s}`,
          ck ? { headers: { "x-hc-cookie": ck } } : undefined,
        );
        const json = await res.json();
        if (!res.ok || json.error) {
          setError(json.hint || json.error || `HTTP ${res.status}`);
          if (json.error === "cookie_burned_or_blocked") setShowCookie(true);
          setVenues([]);
          return;
        }
        setVenues(json.venues || []);
      } catch (e: any) {
        setError(e?.message || "fetch failed");
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Draw markers whenever venues / center change.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();

    // User position marker.
    userMarkerRef.current = L.marker([center.lat, center.lng], {
      title: "You",
    }).addTo(layer);
    userMarkerRef.current.bindPopup("<b>You are here</b>");

    for (const v of venues) {
      const m = L.circleMarker([v.lat, v.lng], {
        radius: 7,
        color: "#fff",
        weight: 1,
        fillColor: colorFor(v),
        fillOpacity: 0.9,
      }).addTo(layer);
      const stars = v.rating != null ? ` ⭐ ${v.rating} (${v.reviews ?? 0})` : "";
      m.bindPopup(
        `<b>${escapeHtml(v.name || "?")}</b>${stars}<br>` +
          `<small>${escapeHtml(v.type || "")}${v.address ? " · " + escapeHtml(v.address) : ""}</small>` +
          (v.url ? `<br><a href="${v.url}" target="_blank" rel="noopener">HappyCow ↗</a>` : ""),
      );
    }
    map.setView([center.lat, center.lng]);
  }, [venues, center]);

  // First load.
  useEffect(() => {
    fetchVenues(center.lat, center.lng, radius, cookie);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const useMyPosition = () => {
    if (!navigator.geolocation) {
      setError("Geolocation not supported by this browser.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(c);
        fetchVenues(c.lat, c.lng, radius, cookie);
      },
      (err) => {
        setLoading(false);
        setError(`Location denied: ${err.message}`);
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  };

  const saveCookie = () => {
    try {
      localStorage.setItem(COOKIE_KEY, cookie);
    } catch {
      /* ignore */
    }
    setShowCookie(false);
    fetchVenues(center.lat, center.lng, radius, cookie);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100dvh" }}>
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          flexWrap: "wrap",
          padding: 10,
          borderBottom: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        <strong>🌱 Veggie map</strong>
        <button onClick={useMyPosition} style={btn}>
          📍 Use my position
        </button>
        <label style={{ fontSize: 14 }}>
          Radius&nbsp;
          <input
            type="number"
            min={1}
            max={25}
            value={radius}
            onChange={(e) => setRadius(Number(e.target.value) || 1)}
            style={{ width: 56, padding: 4 }}
          />
          &nbsp;mi
        </label>
        <button onClick={() => fetchVenues(center.lat, center.lng, radius, cookie)} style={btn}>
          🔄 Search here
        </button>
        <span style={{ fontSize: 13, color: "#6b7280" }}>
          {loading ? "Loading…" : `${venues.length} places`}
        </span>
        <button onClick={() => setShowCookie((s) => !s)} style={{ ...btn, marginLeft: "auto" }}>
          🍪 Cookie
        </button>
      </div>

      {error && (
        <div style={{ padding: 8, background: "#fef2f2", color: "#b91c1c", fontSize: 14 }}>
          {error}
        </div>
      )}

      {showCookie && (
        <div style={{ padding: 10, background: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
          <div style={{ fontSize: 13, marginBottom: 6 }}>
            Paste a fresh HappyCow cookie (whole <code>Cookie:</code> header value). Stored locally,
            sent only with your requests.
          </div>
          <textarea
            value={cookie}
            onChange={(e) => setCookie(e.target.value)}
            rows={3}
            style={{ width: "100%", fontFamily: "monospace", fontSize: 12, padding: 6 }}
            placeholder="PHPSESSID=…; incap_ses_…=…; visid_incap_…=…"
          />
          <button onClick={saveCookie} style={{ ...btn, marginTop: 6 }}>
            Save & reload
          </button>
        </div>
      )}

      <div ref={mapEl} style={{ flex: 1, minHeight: 0 }} />
    </div>
  );
}

const btn: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  background: "#f9fafb",
  cursor: "pointer",
  fontSize: 14,
};

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

export default VeggieMap;
